import { apiClient } from '@/services/api'
import type { WlTokensOverride } from '@/wl-tokens/types'
import type { WlBrandContent } from '@/wl-tokens/pack-content'

/**
 * Server-returned shape for the active pack of a shop.
 *
 * Delivered by `GET /shop/{shopId}/brand-pack/active` (Phase 2 endpoint).
 * Returns 204 No Content when the shop has no pack — the store then leaves
 * `activePack` null and the app falls back to the default engine.
 */
export interface ActiveBrandPack {
  packId: string
  version: number
  engineId: string
  engineVersionRange: string
  /** URL prefix for the pack's uploaded assets. Absolute (S3 signed base). */
  assetsBaseUrl: string
  /** The pack's design tokens — merged over engine defaults + DEFAULT_WL_TOKENS
   *  at inject time. Partial by contract — packs only set what they override. */
  tokens: WlTokensOverride | null
  /** The pack's declarative content — hero, aisles, promises, marquee, etc.
   *  Every field optional; engine renders sensible fallbacks for missing sections. */
  content: WlBrandContent | null
}

export interface UploadPackResponse {
  packId: string
  version: number
  status: 'draft' | 'validated' | 'active' | 'archived'
  validationWarnings: string[]
}

/**
 * Client for the backend brand-pack endpoints.
 *
 * All calls are shop-scoped and (except `getActive`) require the caller to
 * own the shop (Provider role) OR be an admin. Server enforces via
 * `IShopAuthorizationService`.
 */
export const brandPackService = {
  /**
   * Public — no auth required. Fetches the currently-active pack for a shop
   * OR a specific version if `previewPackId` is passed (auth-gated on
   * server for non-active versions).
   *
   * Returns null on 204 (no pack) or any error.
   */
  async getActive(shopId: string, previewPackId?: string): Promise<ActiveBrandPack | null> {
    try {
      const url = previewPackId
        ? `/shop/${shopId}/brand-pack/preview/${previewPackId}`
        : `/shop/${shopId}/brand-pack/active`
      const res = await apiClient.get<ActiveBrandPack | null>(url)
      return res || null
    } catch (err: any) {
      // 204 comes through axios as empty response — treat as no pack
      if (err?.response?.status === 204 || err?.response?.status === 404) return null
      throw err
    }
  },

  /**
   * PRIMARY UPLOAD PATH — partner drops the entire brand-pack folder as a
   * .zip. Server extracts the archive, validates every entry (rejects any
   * scriptable / executable file types), uploads assets to the shop's
   * image storage under a domain-keyed prefix (e.g.
   * `wl-packs/nurpurhalal.co.uk/v3/…`), and creates the pack row with an
   * auto-derived `assetsBaseUrl`.
   *
   * The zip must contain `manifest.json` at the root of the pack folder;
   * every other file (images, fonts) is treated as an asset and uploaded
   * preserving its subfolder structure so the manifest's asset keys resolve
   * unchanged.
   *
   * Doesn't auto-apply — call `apply(shopId, packId)` after the partner
   * previews and clicks Apply.
   */
  async uploadZip(shopId: string, packZip: File): Promise<UploadPackResponse> {
    const form = new FormData()
    form.append('packZip', packZip)
    return await apiClient.post<UploadPackResponse>(
      `/shop/${shopId}/brand-pack/upload-zip`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } } as any,
    )
  },

  /**
   * Advanced-user JSON upload — for partners who host assets themselves
   * (e.g. their own CDN). Send the raw manifest.json string + the absolute
   * `assetsBaseUrl` where relative asset keys should resolve. Prefer
   * `uploadZip` for the standard flow.
   */
  async uploadManifest(
    shopId: string,
    payload: { manifestJson: string, assetsBaseUrl: string },
  ): Promise<UploadPackResponse> {
    return await apiClient.post<UploadPackResponse>(
      `/shop/${shopId}/brand-pack/upload`,
      payload,
    )
  },

  /**
   * List all pack versions for a shop (for the Version history UI).
   */
  async listVersions(shopId: string): Promise<Array<{ packId: string, version: number, engineId: string, status: string, appliedAt: string | null, uploadedAt: string }>> {
    return await apiClient.get(`/shop/${shopId}/brand-pack/versions`)
  },

  /**
   * Apply a specific pack version — sets `Shop.ActiveWlPackId`. Takes effect
   * on next SPA bootstrap for the shop's WL host.
   */
  async apply(shopId: string, packId: string): Promise<void> {
    await apiClient.post(`/shop/${shopId}/brand-pack/apply`, { packId })
  },

  /**
   * Clear the active pack (revert to `default` engine). Doesn't delete
   * the pack — just unsets `Shop.ActiveWlPackId`.
   */
  async clearActive(shopId: string): Promise<void> {
    await apiClient.post(`/shop/${shopId}/brand-pack/clear`, {})
  },
}
